// The same file picked twice in one entry (DEV-3191).
//
// Duplicates are refused by the backend at /files/initiate, but both of its checks — this job's
// own submitted entries, and every other job through crowdFileKeys (DEV-3046) — only see entries
// that have been submitted. Nothing on the page has been submitted yet, so two inputs of the entry
// being filled in can hold the same file and the server has no way to know.
//
// It went unnoticed while an upload started the moment its file was picked: the first one was
// usually finished, and the entry submitted, before the same file could be picked again. Since the
// uploads are queued six at a time (DEV-3170) a file can sit waiting for many minutes, which is
// plenty of time for a moderator to pick it a second time — which is how this was reported.
//
// So the page keeps its own register, and every input claims its file in it twice:
//
//   - the moment the file is picked, by what the picker can say about it without reading a byte.
//     This is the check that has to catch the reported case: the second copy is picked while the
//     first is still being validated or waiting in the queue, long before anything knows its hash.
//   - once the upload queue has hashed it, by that hash — the real identity, which also catches
//     the same recording arriving under two different names.
//
// The key an upload ends up with is `<sha256>.<ext>`, so the hash the queue has just computed can
// be compared both against the files this entry already holds and against the ones still on their
// way up.

// Input (`formId:inputId`) -> {identity, hash} for the file it holds. One entry per input: picking
// a different file replaces the claim, which is what frees the previous file for another input.
const claims = new Map();

// Identifies an input in the page-wide register. The form id is in it because an entry being
// redone opens its own form alongside the page's.
export function uploadOwner(formId, inputId) {
    return `${formId}:${inputId}`;
}

// The hash part of a stored file key. Answers hold the key, the queue hands us the bare hash.
export function hashOfKey(key) {
    return (key || "").split(".")[0];
}

// All the file picker knows about a file before anything reads it. Two picks of the same file on
// the same device agree on all three; two different files practically never do.
function identityOf(file) {
    return `${file.name}:${file.size}:${file.lastModified}`;
}

function heldByAnotherInput(owner, field, value) {
    for (const [otherOwner, claim] of claims) {
        if (otherOwner !== owner && claim[field] === value) {
            return true;
        }
    }
    return false;
}

export function releaseClaim(owner) {
    claims.delete(owner);
}

// Claims the file this input has just been handed, before it is validated or queued. Returns false
// without touching the register when another input on the page already holds the same file.
//
// Replaces whatever this input claimed before, hash included: that file is not this input's any
// more, so it must stop being held against the others.
export function claimSelection(file, owner) {
    const identity = identityOf(file);

    if (heldByAnotherInput(owner, "identity", identity)) {
        return false;
    }
    claims.set(owner, {identity});
    return true;
}

// Claims this hash for this input, unless another input on the page is already uploading it.
// Returns false without touching the register when it is taken.
export function claimHash(hash, owner) {
    if (heldByAnotherInput(owner, "hash", hash)) {
        return false;
    }
    claims.set(owner, {...claims.get(owner), hash});
    return true;
}

export const DUPLICATE_IN_ENTRY_MESSAGE = "You have already added this file to this entry.";

// Throws the same shaped error the server's 409 produces, so every caller already handles it.
// `files` is the entry's answers: a file already uploaded into another input is caught here rather
// than by the register, which keeps working after a re-render or a draft reopened in a new tab.
export function assertNotDuplicateInEntry(hash, owner, { files = [], inputId } = {}) {
    const takenByAnotherInput = files.some(({ id, key }) => id !== inputId && key && hashOfKey(key) === hash);

    if (takenByAnotherInput || !claimHash(hash, owner)) {
        const error = new Error(DUPLICATE_IN_ENTRY_MESSAGE);
        error.code = "DUPLICATE";
        throw error;
    }
}

// Test seam: the register is module state shared by the whole page, so a test that leaves a claim
// in it would leak into the next one.
export function __resetClaimsForTests() {
    claims.clear();
}
